import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useDraft from "../hooks/useDraft";
import DraftBanner from "../components/DraftBanner";
import { RECEIPT_ACCEPT, apiError, formatAmount, receiptProblem, todayIso } from "../lib/finance";
import { CURRENCIES, capExceeded, claimPayload, claimProblem, emptyClaim } from "../lib/claims";
import { ReceiptPreviewDialog, receiptSource } from "../components/finance/ReceiptPreview";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Banknote, Camera, FileImage, Lightbulb, Loader2, X } from "lucide-react";
import { useBottomBar } from "../hooks/useCopilotDock";

const NONE = "__none__";

// Note de frais: declared from the phone, receipt first. `?mission=` prefills the mission (from the mission page);
// `?pour=<fiche>` is an organiser declaring for a colleague without account of that mission (the server checks both).
export default function ClaimFormPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  useBottomBar(true, { hideOnMobile: true, raise: false }); // its actions are fixed at the bottom of a phone only
  const location = useLocation();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [form, setForm] = useState(() => ({ ...emptyClaim("XOF"), mission_id: params.get("mission") || "", claimant_id: params.get("pour") || "" }));
  const [version, setVersion] = useState(null);
  const [receipts, setReceipts] = useState([]); // [{id, name, local?: File}]
  const [categories, setCategories] = useState(null);
  const [missions, setMissions] = useState([]);
  const [phase, setPhase] = useState(isEdit ? "loading" : "ready");
  const [busy, setBusy] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [review, setReview] = useState(null);
  const [preview, setPreview] = useState(null);
  const keyRef = useRef(crypto.randomUUID());
  const fileInput = useRef(null);
  const forName = location.state?.claimantName || "";
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); keyRef.current = crypto.randomUUID(); };
  const draft = useDraft({
    name: "claim-new", userId: user?.id, values: form, enabled: !isEdit && phase === "ready",
    apply: (saved) => setForm((f) => ({ ...f, ...saved, mission_id: params.get("mission") || saved.mission_id || "", claimant_id: params.get("pour") || saved.claimant_id || "" })),
  });

  useEffect(() => {
    api.get("/finance/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
    if (user?.features?.missions) {
      api.get("/missions", { params: { mine: true, limit: 50 } }).then(({ data }) => setMissions(data.items)).catch(() => setMissions([]));
    }
  }, [user]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/finance/claims/${id}`).then(({ data }) => {
      if (!data.capabilities.edit) { setPhase("readonly"); return; }
      setForm({ title: data.title, description: data.description || "", incurred_at: data.incurred_at, amount: data.amount, currency: data.currency,
        category_id: data.category_id, supplier: data.supplier || "", mission_id: data.mission?.id || "", claimant_id: data.claimant.collaborator_id });
      setReceipts(data.attachments.filter((a) => !a.unavailable).map((a) => ({ id: a.id, name: a.name })));
      setVersion(data.version);
      setReview(data.review);
      setPhase("ready");
    }).catch((e) => { setError(apiError(e)); setPhase("error"); });
  }, [id, isEdit]);

  const capWarning = capExceeded((categories || []).find((c) => c.id === form.category_id), form.amount, form.currency);
  const missionChoices = useMemo(() => {
    const list = missions.filter((m) => ["planned", "in_progress", "completed"].includes(m.status));
    if (form.mission_id && !list.some((m) => m.id === form.mission_id)) list.unshift({ id: form.mission_id, reference: "Mission choisie", title: "" });
    return list;
  }, [missions, form.mission_id]);

  const addReceipts = async (files) => {
    setUploading(true);
    for (const file of files) {
      const problem = receiptProblem(file);
      if (problem) { toast.error(problem); continue; }
      try {
        const data = new FormData();
        data.append("file", file);
        const res = await api.post("/finance/attachments", data, { headers: { "Idempotency-Key": crypto.randomUUID() } });
        setReceipts((r) => [...r, { id: res.data.id, name: file.name, local: file }]);
        keyRef.current = crypto.randomUUID();
      } catch (e) {
        toast.error(apiError(e));
      }
    }
    setUploading(false);
    if (fileInput.current) fileInput.current.value = "";
  };

  const save = async (submitAfter) => {
    const problem = claimProblem(form, { today: todayIso(), receipts: receipts.length, submitting: submitAfter });
    if (problem) { setError(problem); return; }
    setError("");
    setBusy(submitAfter ? "submit" : "save");
    try {
      const payload = claimPayload(form, receipts.map((r) => r.id));
      let claim;
      if (isEdit) {
        ({ data: claim } = await api.put(`/finance/claims/${id}`, { ...payload, expected_version: version }));
      } else {
        ({ data: claim } = await api.post("/finance/claims", payload, { headers: { "Idempotency-Key": keyRef.current } }));
      }
      if (submitAfter) {
        await api.post(`/finance/claims/${claim.id}/submit`, { expected_version: claim.version });
        toast.success("Note de frais envoyée pour approbation");
      } else {
        toast.success("Brouillon enregistré");
      }
      draft.clear();
      navigate(form.mission_id && location.state?.back ? location.state.back : "/finance?tab=claims", { replace: true });
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (phase === "loading") return <div className="space-y-3 max-w-xl"><Skeleton className="h-8 w-60" /><Skeleton className="h-96 w-full" /></div>;
  if (phase === "error" || phase === "readonly") {
    return (
      <Card className="p-8 text-center space-y-3 max-w-xl mx-auto">
        <AlertTriangle className="mx-auto text-slate-300" />
        <p className="text-sm text-slate-700">{phase === "readonly" ? "Cette note de frais n'est plus modifiable." : error}</p>
        <Button variant="outline" asChild><Link to="/finance?tab=claims">Retour à mes frais</Link></Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4 pb-24 md:pb-0" data-testid="claim-form">
      <Link to="/finance?tab=claims" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ArrowLeft size={14} /> Mes frais</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">{isEdit ? "Corriger la note de frais" : "Nouvelle note de frais"}</h1>
        <p className="text-sm text-slate-500">{forName ? `Pour ${forName} (sans compte CRM).` : "Ce que vous avez payé pour Jamora, avec le reçu."}</p>
      </div>
      <DraftBanner pending={draft.pending} onRestore={draft.restore} onDismiss={draft.dismiss} />
      <div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-[minmax(0,48rem)_300px]">
      <div className="space-y-4 min-w-0">
      {review?.status === "to_correct" && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="claim-to-correct">À corriger : {review.reason}</Card>
      )}

      <Card className="p-4 space-y-3">
        <Label>Reçu *</Label>
        {receipts.length > 0 && (
          <ul className="space-y-2" data-testid="claim-receipts">
            {receipts.map((r) => (
              <li key={r.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2">
                <FileImage size={18} className="shrink-0 text-slate-400" />
                <button type="button" className="min-w-0 flex-1 truncate text-left text-sm text-slate-700 hover:underline"
                  onClick={() => setPreview({ source: r.local ? receiptSource.local(r.local) : receiptSource.attachment(r.id), name: r.name })}>{r.name}</button>
                <Button variant="ghost" size="icon" aria-label={`Retirer ${r.name}`} onClick={() => setReceipts((list) => list.filter((x) => x.id !== r.id))}><X size={15} /></Button>
              </li>
            ))}
          </ul>
        )}
        <input ref={fileInput} type="file" className="hidden" accept={RECEIPT_ACCEPT} multiple capture="environment"
          onChange={(e) => addReceipts(Array.from(e.target.files || []))} data-testid="claim-receipt-input" />
        <Button type="button" variant="outline" className="h-11 w-full" disabled={uploading} onClick={() => fileInput.current?.click()} data-testid="claim-receipt-add">
          {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Camera size={16} className="mr-2" />}Prendre ou joindre le reçu
        </Button>
      </Card>

      <Card className="p-4 space-y-4">
        <div className="space-y-1.5"><Label htmlFor="claim-title">Dépense *</Label>
          <Input id="claim-title" maxLength={180} className="text-base md:text-sm" placeholder="Ex. : Taxi jusqu'à la pharmacie" value={form.title}
            onChange={(e) => set("title", e.target.value)} data-testid="claim-title" /></div>
        <div className="grid grid-cols-[1fr_7rem] gap-3">
          <div className="space-y-1.5"><Label htmlFor="claim-amount">Montant *</Label>
            <Input id="claim-amount" inputMode="decimal" className="text-base md:text-sm" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="claim-amount" /></div>
          <div className="space-y-1.5"><Label>Devise</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="claim-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label htmlFor="claim-date">Date *</Label>
            <Input id="claim-date" type="date" max={todayIso()} className="text-base md:text-sm" value={form.incurred_at} onChange={(e) => set("incurred_at", e.target.value)} data-testid="claim-date" /></div>
          <div className="space-y-1.5"><Label>Catégorie *</Label>
            <Select value={form.category_id} onValueChange={(v) => set("category_id", v)} disabled={!categories}>
              <SelectTrigger data-testid="claim-category"><SelectValue placeholder={categories?.length === 0 ? "Aucune catégorie" : "Choisir…"} /></SelectTrigger>
              <SelectContent>{(categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        {capWarning && (
          <p className="text-sm text-amber-900 rounded-md bg-amber-50 p-3" data-testid="claim-cap-warning">
            Au-dessus du plafond de cette catégorie ({formatAmount(capWarning, form.currency)} par note). Vous pouvez l'envoyer : la personne qui approuve le verra.
          </p>
        )}
        {user?.features?.missions && !form.claimant_id && (
          <div className="space-y-1.5"><Label>Mission</Label>
            <Select value={form.mission_id || NONE} onValueChange={(v) => set("mission_id", v === NONE ? "" : v)}>
              <SelectTrigger data-testid="claim-mission"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Aucune mission</SelectItem>
                {missionChoices.map((m) => <SelectItem key={m.id} value={m.id}>{m.reference}{m.title ? ` · ${m.title}` : ""}</SelectItem>)}
              </SelectContent>
            </Select></div>
        )}
        <div className="space-y-1.5"><Label htmlFor="claim-supplier">Fournisseur</Label>
          <Input id="claim-supplier" maxLength={180} className="text-base md:text-sm" placeholder="Facultatif" value={form.supplier} onChange={(e) => set("supplier", e.target.value)} /></div>
        <div className="space-y-1.5"><Label htmlFor="claim-description">Précisions</Label>
          <Textarea id="claim-description" rows={2} maxLength={2000} className="text-base md:text-sm" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
        {error && <p role="alert" className="text-sm text-red-700" data-testid="claim-error">{error}</p>}
      </Card>

      {/* Phone: actions fixed above the bottom navigation, so they stay at hand while scrolling. Larger screens: at the end of the form. */}
      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-slate-200 bg-white p-3 md:static md:border-0 md:bg-transparent md:p-0">
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" asChild className="hidden md:inline-flex"><Link to="/finance?tab=claims">Annuler</Link></Button>
          <Button variant="outline" onClick={() => save(false)} disabled={!!busy || uploading} className="flex-1 md:flex-none" data-testid="claim-save">
            {busy === "save" && <Loader2 size={14} className="mr-1.5 animate-spin" />}<span className="md:hidden">Brouillon</span><span className="hidden md:inline">Enregistrer le brouillon</span>
          </Button>
          <Button onClick={() => save(true)} disabled={!!busy || uploading} className={`flex-1 md:flex-none`} data-testid="claim-send">
            {busy === "submit" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Envoyer pour approbation
          </Button>
        </div>
      </div>
      </div>

      <aside className="hidden lg:block space-y-4 lg:sticky lg:top-4">
        <Card className="p-4 space-y-2 bg-slate-50 border-slate-200">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Lightbulb size={15} className="text-jamora" /> Comment ça se passe</div>
          <p className="text-xs text-slate-600">Le brouillon reste modifiable. Une fois envoyée, une autre personne approuve, refuse ou vous renvoie la note à corriger avec un motif. Approuvée, elle vous est remboursée.</p>
        </Card>
        <Card className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Banknote size={15} className="text-jamora" /> Avance reçue ?</div>
          <p className="text-xs text-slate-600">Si on vous a remis une avance pour la mission, vos frais approuvés sont d'abord pris dessus. Vous suivez le solde dans « Mes frais ».</p>
        </Card>
      </aside>
      </div>

      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Reçu de la note de frais." />
    </div>
  );
}
